import React, { useId, useState } from 'react';
import styled from '@emotion/styled';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import { useRouteChallengeTeamMembers } from '../../../Hooks/useChallengeQueries';
import { useLanguage } from '../../../Hooks/useLanguage';
import { useTrans } from '../../../Store/System/LocalizationStore';
import type { RouteChallengeTeam } from '../../../Model/Engagement/RouteChallenge/RouteChallengeTeam';
import type { RouteChallenge } from '../../../Model/Engagement/RouteChallenge/RouteChallenge';
import { getMediaSmallest } from '../../../Model/Media/Media';
import { Theme } from '../../../Theme/Theme';

type MemberCopy = {
  view: string; title: string; member: string; points: string; extra: string;
  description: string; loading: string; failed: string; retry: string; empty: string; unavailable: string;
};

const copy: Record<string, MemberCopy> = {
  en: {
    view: 'View members of {{team}}', title: 'Team members', member: 'Member', points: 'Total points', extra: 'Extra points',
    description: 'Extra points are earned beyond the team goal.', loading: 'Loading members…',
    failed: 'Could not load team members.', retry: 'Try again', empty: 'No members yet.', unavailable: 'Extra points unavailable',
  },
  de: {
    view: 'Mitglieder von {{team}} anzeigen', title: 'Teammitglieder', member: 'Mitglied', points: 'Punkte', extra: 'Zusatz',
    description: 'Zusatzpunkte werden über das Teamziel hinaus gesammelt.', loading: 'Mitglieder werden geladen…',
    failed: 'Die Teammitglieder konnten nicht geladen werden.', retry: 'Erneut versuchen', empty: 'Noch keine Mitglieder.', unavailable: 'Zusatzpunkte nicht verfügbar',
  },
  fr: {
    view: 'Voir les membres de {{team}}', title: 'Membres de l’équipe', member: 'Membre', points: 'Total des points', extra: 'Points supplémentaires',
    description: 'Les points supplémentaires sont gagnés au-delà de l’objectif de l’équipe.', loading: 'Chargement des membres…',
    failed: 'Impossible de charger les membres de l’équipe.', retry: 'Réessayer', empty: 'Aucun membre pour le moment.', unavailable: 'Points supplémentaires indisponibles',
  },
  it: {
    view: 'Mostra i membri di {{team}}', title: 'Membri della squadra', member: 'Membro', points: 'Punti totali', extra: 'Punti extra',
    description: 'I punti extra si guadagnano oltre l’obiettivo della squadra.', loading: 'Caricamento dei membri…',
    failed: 'Impossibile caricare i membri della squadra.', retry: 'Riprova', empty: 'Nessun membro per ora.', unavailable: 'Punti extra non disponibili',
  },
};

const TeamButton = styled.button`
  appearance: none;
  background: none;
  border: 0;
  color: inherit;
  cursor: pointer;
  display: block;
  font: inherit;
  max-width: 100%;
  overflow: hidden;
  padding: 0;
  text-align: start;
  text-decoration: underline;
  text-decoration-color: currentColor;
  text-underline-offset: 3px;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    text-decoration-thickness: 2px;
  }

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 3px;
    border-radius: 2px;
  }
`;

const MembersModal = styled(Modal)`
  .modal-content {
    border-radius: 16px;
    color: ${Theme.Colors.textColor};
  }

  .modal-title {
    overflow-wrap: anywhere;
  }
`;

const Description = styled.p`
  color: ${Theme.Colors.charcoal};
  font-size: 14px;
  margin-bottom: 20px;
`;

const MemberTable = styled.table`
  width: 100%;
  table-layout: fixed;
  font-variant-numeric: tabular-nums;

  caption {
    caption-side: top;
    color: ${Theme.Colors.textColor};
    font-size: 18px;
    font-weight: 600;
    padding: 0 0 12px;
  }

  th, td {
    padding: 12px 8px;
    vertical-align: middle;
    overflow-wrap: anywhere;
  }

  th {
    color: ${Theme.Colors.charcoal};
    font-size: 12px;
    font-weight: 500;
    border-bottom: 1px solid ${Theme.Colors.lighterGrey};
  }

  td {
    font-size: 14px;
    border-bottom: 1px solid ${Theme.Colors.lighterGrey};
  }

  th:first-of-type, td:first-of-type {
    padding-left: 0;
  }

  th:not(:first-of-type), td:not(:first-of-type) {
    width: 25%;
    text-align: end;
  }

  th:last-child, td:last-child {
    padding-right: 0;
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }
`;

const MemberInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const Avatar = styled.div<{ $imageUrl?: string }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ $imageUrl }) => $imageUrl ? `url(${$imageUrl}) center / cover` : Theme.Colors.lightestPrimary};
  color: ${Theme.Colors.textColor};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ExtraPoints = styled.span`
  color: ${Theme.Colors.primaryDarkest};
  font-weight: 600;
`;

const Status = styled.div`
  color: ${Theme.Colors.charcoal};
  padding: 24px 0;
  text-align: center;
`;

const RetryButton = styled.button`
  background: none;
  border: 1px solid ${Theme.Colors.charcoal};
  border-radius: 8px;
  color: ${Theme.Colors.textColor};
  margin-top: 12px;
  padding: 8px 16px;
`;

export const ChallengeTeamMembers = ({ team, challenge }: { team: RouteChallengeTeam; challenge: Pick<RouteChallenge, 'started' | 'totalPossiblePoints'> }) => {
  const { t } = useTrans();
  const { language } = useLanguage();
  const text = copy[language.toLowerCase().split('-')[0]] ?? copy.en;
  const [isOpen, setIsOpen] = useState(false);
  const titleId = useId();
  const { data: members = [], isLoading, isError, refetch } = useRouteChallengeTeamMembers(isOpen ? team.id : undefined);
  const close = () => setIsOpen(false);
  const showExtraPoints = challenge.started && challenge.totalPossiblePoints > 0 && team.totalCollectedPoints >= challenge.totalPossiblePoints;

  return (
    <>
      <TeamButton
        type="button"
        onClick={() => setIsOpen(true)}
        aria-label={t('challenge.teamMembers.view', { defaultValue: text.view, team: team.name })}
        aria-haspopup="dialog"
      >
        {team.name}
      </TeamButton>
      <MembersModal isOpen={isOpen} toggle={close} labelledBy={titleId} centered scrollable>
        <ModalHeader id={titleId} toggle={close}>{team.name}</ModalHeader>
        <ModalBody>
          {isLoading ? (
            <Status role="status">{t('challenge.teamMembers.loading', { defaultValue: text.loading })}</Status>
          ) : isError ? (
            <Status role="alert">
              <div>{t('challenge.teamMembers.failed', { defaultValue: text.failed })}</div>
              <RetryButton type="button" onClick={() => { void refetch(); }}>
                {t('challenge.teamMembers.retry', { defaultValue: text.retry })}
              </RetryButton>
            </Status>
          ) : members.length === 0 ? (
            <Status>{t('challenge.teamMembers.empty', { defaultValue: text.empty })}</Status>
          ) : (
            <>
              {showExtraPoints && <Description>{t('challenge.teamMembers.description', { defaultValue: text.description })}</Description>}
              <MemberTable>
                <caption>{t('challenge.teamMembers.title', { defaultValue: text.title })}</caption>
                <thead>
                  <tr>
                    <th scope="col">{t('challenge.teamMembers.member', { defaultValue: text.member })}</th>
                    <th scope="col">{t('challenge.teamMembers.points', { defaultValue: text.points })}</th>
                    {showExtraPoints && <th scope="col">{t('challenge.teamMembers.extra', { defaultValue: text.extra })}</th>}
                  </tr>
                </thead>
                <tbody>
                  {members.map((member) => (
                    <tr key={member.id}>
                      <td>
                        <MemberInfo>
                          <Avatar $imageUrl={member.profileImage ? getMediaSmallest(member.profileImage) : undefined} aria-hidden="true">
                            {!member.profileImage && member.username.charAt(0).toUpperCase()}
                          </Avatar>
                          <span>{member.username || t('challenge.anonymous', { defaultValue: 'Participant' })}</span>
                        </MemberInfo>
                      </td>
                      <td>{Math.round(member.totalCollectedPoints).toLocaleString(language)}</td>
                      {showExtraPoints && <td>
                        {member.extraCollectedPoints == null ? (
                          <span aria-label={t('challenge.teamMembers.unavailable', { defaultValue: text.unavailable })} title={t('challenge.teamMembers.unavailable', { defaultValue: text.unavailable })}>—</span>
                        ) : (
                          <ExtraPoints>+{Math.round(member.extraCollectedPoints).toLocaleString(language)}</ExtraPoints>
                        )}
                      </td>}
                    </tr>
                  ))}
                </tbody>
              </MemberTable>
            </>
          )}
        </ModalBody>
      </MembersModal>
    </>
  );
};
